import type { Service } from '../types/service'

export const services: Service[] = [
  {
    slug: 'trust-administration',
    path: '/trust-administration',
    name: 'Trust Administration',
    short:
      'Managing trust assets, distributions, records, expenses and beneficiary matters in accordance with the terms of the trust.',
    intro:
      'Professional administration of trust assets, distributions, records and beneficiary matters with careful attention to the terms of the trust.',
    image:
      'https://cdn.magicpatterns.com/patterns/generated-images/4a1df6d4-af01-4af1-9c15-c97f08f472bd.jpg',
    imageAlt:
      'Organized trust and estate documents with reading glasses and a pen on a walnut desk',
    items: [
      'Trust asset administration',
      'Beneficiary distributions',
      'Bill and expense administration',
      'Record keeping',
      'Financial administration',
      'Beneficiary communication',
      'Coordination with attorneys and accountants',
      'Ongoing trust administration',
    ],
  },
  {
    slug: 'special-needs-trust-administration',
    path: '/special-needs-trust-administration',
    name: 'Special Needs Trust Administration',
    short:
      'Managing trust assets and distributions for individuals with disabilities while coordinating with families and appropriate professional advisors.',
    intro:
      'Professional administration and coordination of trust assets for individuals with disabilities or special needs.',
    image:
      'https://cdn.magicpatterns.com/patterns/generated-images/56f3c3b0-d677-491d-a674-ee689cdfce01.jpg',
    imageAlt: 'A family talking together at home in a comfortable living room',
    items: [
      'Trust administration',
      'Asset and expense administration',
      'Distribution management',
      'Record keeping',
      'Family communication',
      'Beneficiary support',
      'Coordination with professional advisors',
    ],
    note: 'Because benefit program eligibility is governed by each individual\u2019s circumstances and applicable rules, we administer distributions carefully and coordinate with the family\u2019s attorney and advisors throughout.',
  },
  {
    slug: 'poa-ahcd-services',
    path: '/poa-ahcd-services',
    name: 'POA & AHCD Services',
    short:
      'Assisting with financial affairs, personal matters, healthcare coordination and other responsibilities under properly established authority.',
    intro:
      'Professional support with financial, personal and healthcare-related responsibilities under properly established legal authority.',
    image:
      'https://cdn.magicpatterns.com/patterns/generated-images/5600c209-b81d-49db-ad4e-33ca9a5491a3.jpg',
    imageAlt: 'A small private client meeting with paperwork on the table',
    groups: [
      {
        title: 'Power of Attorney',
        intro: 'Support with financial and administrative responsibilities.',
        items: [
          'Financial administration',
          'Bill payments',
          'Personal affairs',
          'Administrative matters',
          'Account coordination',
          'Communication with relevant professionals',
        ],
      },
      {
        title: 'Advance Health Care Directives',
        intro: 'Support with healthcare-related coordination and communication.',
        items: [
          'Healthcare coordination',
          'Appointment coordination',
          'Communication with healthcare providers',
          'Personal wishes and care information',
          'Family communication',
        ],
      },
    ],
    note: 'We act within the authority established in the governing documents, and coordinate with the attorneys and healthcare professionals involved so that legal and medical matters are addressed by the right professional.',
  },
  {
    slug: 'conservatorship-services',
    path: '/conservatorship-services',
    name: 'Conservatorship Services',
    short:
      'Managing financial, personal and administrative responsibilities for individuals under a court-authorized conservatorship.',
    intro:
      'Professional administration and support for individuals whose financial or personal affairs are subject to a court-authorized conservatorship.',
    image:
      'https://cdn.magicpatterns.com/patterns/generated-images/da897203-ec38-4f7a-9fa7-1224b019c96b.jpg',
    imageAlt: 'A quiet private office with bookshelves and a conference table',
    items: [
      'Financial administration',
      'Bill payments',
      'Asset administration',
      'Record keeping',
      'Personal affairs',
      'Administrative coordination',
      'Family communication',
      'Coordination with attorneys and other professionals',
    ],
    note: 'Services are provided in accordance with the authority established in each matter and applicable state law.',
  },
  {
    slug: 'probate-administration',
    path: '/probate-administration',
    name: 'Probate Administration',
    short:
      'Managing estate assets, bank accounts, documentation, expenses, obligations and beneficiary distributions throughout the probate process.',
    intro:
      'Professional administration of estate assets, accounts, expenses, documentation and beneficiary matters during the probate process.',
    image:
      'https://cdn.magicpatterns.com/patterns/generated-images/13b3fffd-3cba-441f-b538-688324951851.jpg',
    imageAlt:
      'A fiduciary advisor reviewing estate documents with a couple at a table',
    items: [
      'Estate asset administration',
      'Bank account administration',
      'Estate expenses',
      'Record keeping',
      'Documentation',
      'Creditor and obligation administration',
      'Beneficiary communication',
      'Distribution administration',
      'Coordination with attorneys and accountants',
    ],
  },
  {
    slug: 'personal-private-fiduciary-care',
    path: '/personal-private-fiduciary-care',
    name: 'Personal & Private Fiduciary Care',
    short:
      'Handling bill payments, housing arrangements, personal affairs, healthcare coordination and day-to-day administrative needs for seniors or individuals requiring assistance.',
    intro:
      'Handling bill payments, housing arrangements, personal affairs, healthcare coordination and day-to-day administrative needs for seniors or individuals requiring assistance.',
    image:
      'https://cdn.magicpatterns.com/patterns/generated-images/9fca1ec2-4a83-4352-8c3c-a0d1a5ae1a3e.jpg',
    imageAlt:
      'An older gentleman sorting household mail at his kitchen table with assistance',
    items: [
      'Bill payment administration',
      'Household and housing coordination',
      'Personal affairs administration',
      'Healthcare coordination',
      'Appointment coordination',
      'Document organization',
      'Communication with family members',
      'Coordination with professional service providers',
      'Day-to-day administrative support',
    ],
  },
  {
    slug: 'care-management-services',
    path: '/care-management-services',
    name: 'Care Management Services',
    short:
      'Coordinating personal care, appointments, healthcare providers, housing, daily needs and communication with families and care professionals.',
    intro:
      'Coordinating the personal, healthcare and daily support needs of individuals while keeping families and relevant professionals informed.',
    image:
      'https://cdn.magicpatterns.com/patterns/generated-images/8d62bd1d-6158-4e6e-a4c8-447e4447592c.jpg',
    imageAlt:
      'A care coordinator sitting with an elderly woman reviewing a schedule at home',
    items: [
      'Care coordination',
      'Healthcare appointments',
      'Communication with healthcare providers',
      'Housing coordination',
      'Daily needs',
      'Family communication',
      'Service-provider coordination',
      'Personal support administration',
    ],
  },
]

export const getService = (slug: string): Service | undefined =>
  services.find((service) => service.slug === slug)
